import React, { useEffect, useState } from "react";
import PageItem from "./PageItem";
import SwitchPageItem from "./SwitchPageItem";

interface Props {
  count: number;
  pageSize: number;
  urlParams: URLSearchParams;
  onPageChange: (pageNumber: number) => void;
  useXsView?: boolean;
  className?: string;
}

interface BoundariesAndIndicators {
  leftBoundary: number;
  rightBoundary: number;
  afterFirstPageIndicator: boolean;
  beforeLastPageIndicator: boolean;
}

const Pagination = ({
  count,
  pageSize,
  urlParams,
  onPageChange,
  useXsView,
  className
}: Props) => {
  // state
  const [activePage, setActivePage] = useState(1);

  // variables
  const pagesCount = count > 1 ? Math.ceil(count / pageSize) : 0;
  const siblingsCount = 1;
  const boundaryCount = 2;

  // handlers
  const changePageHandler = (page: number) => {
    onPageChange(page);
    setActivePage(page);
  };

  // helpers
  const getPagesToRender = (): number[] => {
    let pages: number[] = [];
    // boundaries between which pages will be render pages; indicators signalize more (not rendered) pages
    const {
      leftBoundary,
      rightBoundary,
      afterFirstPageIndicator,
      beforeLastPageIndicator,
    } = getBoundariesAndIdicators();

    // first page is visible always
    pages.push(1);

    // render ... indicator after 1st page
    // 0 indicate ... (this is not a real page)
    if (afterFirstPageIndicator) pages.push(0);

    // pages between boundaries
    for (
      let pageNumber = leftBoundary;
      pageNumber <= rightBoundary;
      pageNumber++
    ) {
      pages.push(pageNumber);
    }

    // render ... indicator before last page
    // 0 indicate ... (this is not a real page)
    if (beforeLastPageIndicator) pages.push(0);

    // last page is visible always
    pages.push(pagesCount);

    return pages;
  };

  const getBoundariesAndIdicators = (): BoundariesAndIndicators => {
    let leftBoundary = 0;
    let rightBoundary = 0;
    let afterFirstPageIndicator = false;
    let beforeLastPageIndicator = false;
    // 2 on start is first and last page
    let totalPaginationBlocks = 2 + boundaryCount + 2 * siblingsCount;

    // if count of all pages is lower then all pagination blocks (pages + indicators) => render all pages
    if (pagesCount <= totalPaginationBlocks) {
      leftBoundary = 2;
      rightBoundary = pagesCount - 1;
    } else {
      // first n pages (if active page is on the start)
      if (activePage < 1 + boundaryCount) {
        leftBoundary = 2;
        rightBoundary = 1 + boundaryCount;
        beforeLastPageIndicator = true;
      }
      // last n pages (if active page is on the end)
      else if (activePage > pagesCount - boundaryCount) {
        leftBoundary = pagesCount - boundaryCount;
        rightBoundary = pagesCount - 1;
        afterFirstPageIndicator = true;
        // other pages (if active page is not on the start or on the end)
      } else {
        leftBoundary = activePage - siblingsCount;
        rightBoundary = activePage + siblingsCount;
        afterFirstPageIndicator =
          activePage - siblingsCount >= 1 + boundaryCount;
        beforeLastPageIndicator =
          activePage + siblingsCount <= pagesCount - boundaryCount;
      }
    }

    return {
      leftBoundary: leftBoundary,
      rightBoundary: rightBoundary,
      afterFirstPageIndicator: afterFirstPageIndicator,
      beforeLastPageIndicator: beforeLastPageIndicator,
    };
  };

  const renderPages = () => {
    let pages: JSX.Element[] = [];
    let pageNumbers = getPagesToRender();

    for (let index = 0; index < pageNumbers.length; index++) {
      const pageNumber = pageNumbers[index];

      let isActivePage = pageNumber == activePage;
      let page = (
        <PageItem
          pageNumber={pageNumber}
          isActive={isActivePage}
          handleChangePage={changePageHandler}
          isPagesIndicator={pageNumber == 0}
          useXsView={useXsView}
          key={index}
        />
      );

      pages.push(page);
    }

    return pages;
  };

  const setActivePageFromStore = () => {
    const activePage = urlParams.get("Page");
    if (activePage) setActivePage(Number.parseInt(activePage));
  };

  useEffect(() => {
    setActivePageFromStore();
  }, [urlParams.toString()]);

  // render
  if (pagesCount > 1) {
    return (
      <div className={className}>
        <ul className="pagination mb-0">
          {activePage > 1 && (
            <SwitchPageItem
              pageNumber={activePage - 1}
              direction="prev"
              handleChangePage={changePageHandler}
              useXsView={useXsView}
            />
          )}

          {renderPages()}

          {activePage < pagesCount && (
            <SwitchPageItem
              pageNumber={activePage + 1}
              direction="next"
              handleChangePage={changePageHandler}
              useXsView={useXsView}
            />
          )}
        </ul>
      </div>
    );
  } else {
    return null;
  }
};

export default Pagination;
